// Client view — cream/ink editorial. The client sees ONLY their own dashboard.
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { LogOut, Bell } from "lucide-react";
import { toast } from "sonner";
import { usePortalAuth, PORTAL_API as API, formatApiError } from "./PortalAuth";
import { OverviewPanel, NotesPanel, TasksPanel, FilesPanel } from "./panels";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "notes", label: "Messages" },
  { key: "tasks", label: "Tasks" },
  { key: "files", label: "Files" },
];

export default function ClientView({ user }) {
  const { logout } = usePortalAuth();
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
        setClient(data[0] || null);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  if (!client) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="overline text-ink/55 animate-pulse">Loading your room…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="client-dashboard">
      {/* Header */}
      <header className="border-b border-ink/15 bg-cream/95 backdrop-blur-md sticky top-0 z-40">
        <div className="container-edge py-4 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3">
            <img src="/brand/mmedia-logo-dark.png" alt="M Media Corp" className="h-10 w-auto" draggable="false" />
            <span className="hidden md:block overline text-ink/55">Client Portal</span>
          </Link>
          <div className="flex items-center gap-5">
            <span className="overline text-ink/55 hidden sm:block">{user.name}</span>
            <button onClick={logout} className="overline border border-ink/30 hover:border-ink hover:bg-ink/5 px-3 py-1.5 transition" data-testid="portal-logout">
              <LogOut size={12} className="inline mr-1.5" />Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Hero strip */}
      <section className="container-edge pt-10 md:pt-14 pb-6">
        <div className="overline text-ink/55">Welcome back · {user.name}</div>
        <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.88] mt-3">
          The room <br/>
          <span className="serif-italic font-serif normal-case">we built for you</span>.
        </h1>
        <p className="font-serif italic text-lg md:text-xl text-ink/70 mt-5 max-w-2xl">
          Everything we are building together — notes, tasks, files, and the work we are bringing to you for approval. Private to you and your M Media team.
        </p>
      </section>

      {/* Tabs */}
      <nav className="container-edge sticky top-[72px] z-30 bg-cream/95 backdrop-blur-md border-y border-ink/15">
        <div className="flex gap-2 md:gap-4 overflow-x-auto py-3" data-testid="client-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`overline px-4 py-2 border transition-all whitespace-nowrap ${
                tab === t.key ? "bg-ink text-cream border-ink" : "border-ink/25 text-ink hover:bg-ink/5"
              }`}
              data-testid={`client-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Active panel */}
      <main className="container-edge py-10 md:py-14">
        {tab === "overview" && (
          <OverviewPanel client={client} currentUser={user} tone="light" onSaved={setClient} />
        )}
        {tab === "notes" && <NotesPanel client={client} currentUser={user} tone="light" />}
        {tab === "tasks" && <TasksPanel client={client} currentUser={user} tone="light" />}
        {tab === "files" && <FilesPanel client={client} currentUser={user} tone="light" />}
      </main>
    </div>
  );
}
